/**
 * Executive Producer Quips - Type Definitions
 *
 * Extracted from executiveProducerQuips.ts for better modularity
 * Part of the 3-file split refactoring (2026-01-07)
 */

import type { MessageContext } from '@dramadetector/shared';
import { PatternType } from '../dramaExpert/types';

/**
 * Quip category types for conflict scenarios
 * Maps to the 10 conflict scenario categories in the quip pool
 */
export type QuipCategory =
  | 'conflict'
  | 'escalation'
  | 'anxious'
  | 'boundary'
  | 'avoidant'
  | 'gaslighting'
  | 'codependency'
  | 'power'
  | 'defensiveness'
  | 'withdrawal';

/**
 * Quip entry with optional context targeting
 */
export interface QuipEntry {
  key: string;
  contexts?: MessageContext[]; // If undefined, shows for ALL contexts
  isDogfooding?: boolean; // True if this is a "producer uses DramaDetector" quip
}

/**
 * Maps PatternType enum values to quip categories
 * This enables AI-reactive quip selection based on detected drama patterns
 *
 * Priority: If multiple patterns detected, first match wins
 * Fallback: If no pattern matches, returns undefined (use context-aware or general quips)
 */
export const PATTERN_TO_QUIP_CATEGORY: Record<PatternType, QuipCategory | undefined> = {
  // Attachment Patterns
  [PatternType.ANXIOUS_ATTACHMENT]: 'anxious',
  [PatternType.AVOIDANT_ATTACHMENT]: 'avoidant',
  [PatternType.ANXIOUS_AVOIDANT_CYCLE]: 'anxious', // Map to anxious (primary) or could alternate
  [PatternType.SECURE_ATTACHMENT]: undefined, // Positive pattern, no conflict quip needed

  // Boundary Patterns
  [PatternType.BOUNDARY_VIOLATION]: 'boundary',
  [PatternType.BOUNDARY_EROSION]: 'boundary',
  [PatternType.HEALTHY_BOUNDARY_SETTING]: undefined, // Positive pattern
  [PatternType.CONFLICT_AVOIDANCE]: 'conflict',

  // Communication Patterns
  [PatternType.PASSIVE_AGGRESSIVE]: 'conflict',
  [PatternType.STONEWALLING]: 'withdrawal',
  [PatternType.DEFENSIVENESS]: 'defensiveness',
  [PatternType.CRITICISM]: 'escalation',
  [PatternType.LOVE_BOMBING]: 'anxious',

  // Emotional Patterns
  [PatternType.EMOTIONAL_ESCALATION]: 'escalation',
  [PatternType.EMOTIONAL_WITHDRAWAL]: 'withdrawal',
  [PatternType.TRIANGULATION]: 'power',
  [PatternType.GASLIGHTING_POTENTIAL]: 'gaslighting',

  // Relationship Dynamics
  [PatternType.POWER_IMBALANCE]: 'power',
  [PatternType.CODEPENDENCY]: 'codependency',
  [PatternType.ENMESHMENT]: 'codependency',
  [PatternType.HEALTHY_INTERDEPENDENCE]: undefined, // Positive pattern
};

/**
 * Quip key prefixes for each conflict scenario category
 * Used to filter PRODUCER_QUIPS by category
 */
export const QUIP_CATEGORY_PREFIXES: Record<QuipCategory, string[]> = {
  conflict: ['producer_quips.conflict_'],
  escalation: ['producer_quips.escalation_'],
  anxious: ['producer_quips.anxious_'],
  boundary: ['producer_quips.boundary_'],
  avoidant: ['producer_quips.avoidant_'],
  gaslighting: ['producer_quips.gaslighting_'],
  codependency: ['producer_quips.codependency_'],
  power: ['producer_quips.power_'],
  defensiveness: ['producer_quips.defensiveness_'],
  withdrawal: ['producer_quips.withdrawal_'],
};
